import React, { useState } from 'react';
import { Select, Input, Modal } from '@trackman/web-shared-components';
import { FormDetails, ModalContent, SingleRow } from './NewAddPlaceholderPlayerStyles';
import { Dexterity, ISelectOption, enumToDropdownValuesMap } from '@trackman/baseball-shared-components';

interface IProps {
  onDismissCallback: () => void;
  onAddPlayerCallback: (
    firstName: string,
    lastName: string,
    jerseyNumber: string | undefined,
    foreignId: string,
    pitchingHandedness: Dexterity,
    battingHandedness: Dexterity
  ) => void;
  visible: boolean;
  allowAddingId: boolean;
  parentRef?: HTMLDivElement;
}

export function useReplaceSpecialChars(value: string): string {
  return value.replace(/[,<>?;'"`~!@#$%^&| ]/g, '');
}

export const NewAddPlaceholderPlayer: React.FC<IProps> = ({ onDismissCallback, onAddPlayerCallback, visible, allowAddingId }) => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [foreignId, setForeignId] = useState<string>('');
  const [jerseyNumber, setJerseyNumber] = useState<string | undefined>(undefined);
  const [pitchingHandedness, setPitchingHandedness] = useState<Dexterity>(Dexterity.Undefined);
  const [battingHandedness, setBattingHandedness] = useState<Dexterity>(Dexterity.Undefined);
  const handednessValues = enumToDropdownValuesMap(Dexterity);

  const onJerseyNumberChange = (jerseyNumber: string) => {
    setJerseyNumber(jerseyNumber);
  };

  const getSelectedValue = (options: ISelectOption[], selected: string) => {
    return options.find((x) => x.value === selected);
  };

  const isAnyRequiredEmpty = (): boolean => {
    if (allowAddingId) {
      return !firstName || !lastName || !jerseyNumber || !foreignId || !pitchingHandedness || !battingHandedness;
    }

    return !firstName || !lastName || !jerseyNumber || !pitchingHandedness || !battingHandedness;
  };

  return (
    <Modal
      open={visible}
      onClose={onDismissCallback}
      title='Create placeholder player'
      fullWidth
      confirmBtn={{
        label: 'Create player',
        onClick: () => onAddPlayerCallback(firstName, lastName, jerseyNumber, foreignId, pitchingHandedness, battingHandedness),
        disabled: isAnyRequiredEmpty(),
      }}
      cancelBtn={{
        onClick: onDismissCallback,
      }}
      maxWidth={'md'}
    >
      <ModalContent>
        <FormDetails>Fill in the details for creating a new player. </FormDetails>
        <SingleRow>
          <Input label='First name' onChange={(event) => setFirstName(event.target.value)} autoFocus value={firstName} required />
          <Input label='Last name' value={lastName} onChange={(event) => setLastName(event.target.value)} required />
        </SingleRow>
        <SingleRow>
          {allowAddingId && (
            <Input
              label='ID'
              onChange={(event) => setForeignId(useReplaceSpecialChars(event.target.value))}
              value={foreignId}
              required
              width={'calc(50% - 6px)'}
            />
          )}
          <Input
            label='Jersey number (e.g. 3)'
            onChange={(event) => onJerseyNumberChange(event.target.value.toString())}
            value={jerseyNumber}
            required
            width={'calc(50% - 6px)'}
          />
        </SingleRow>
        <SingleRow>
          <Select
            placeholder='Pitching handedness'
            label={'Pitching handedness'}
            value={pitchingHandedness}
            onChange={(event: any) => setPitchingHandedness(event.target.value)}
            selectOptions={handednessValues}
            width={'50%'}
            required
          />
          <Select
            placeholder='Batting handedness'
            label={'Batting handedness'}
            value={getSelectedValue(handednessValues, battingHandedness)?.value}
            onChange={(event: any) => setBattingHandedness(event.target.value)}
            selectOptions={handednessValues}
            width={'50%'}
            required
          />
        </SingleRow>
      </ModalContent>
    </Modal>
  );
};
